import { devDebug } from "../lib/devLog";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Send,
  MessageCircle,
  Search,
  Plus,
  Lock,
  Loader2,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Mic,
  Square,
  Trash2,
  MoreVertical,
  BellOff,
  Bell,
  Clock3,
  Volume2,
  VolumeX,
  Image as ImageIcon,
  Video as VideoIcon
} from "lucide-react";
import {
  deleteEncryptedMessage,
  fetchMessages,
  listConversations,
  markConversationRead,
  openConversation,
  sendEncryptedMessage,
  setConversationTimer,
} from "../lib/dmApi";
import {
  decryptFromPeer,
  encryptBytesForPeer,
  encryptForPeer,
  getMyDmKeys,
  assertPeerBinding,
  computeSafetyNumber,
  requirePeerBinding,
  PeerBindingError,
} from "../lib/dmCrypto";
import { isConversationMuted, toggleConversationMuted } from "../lib/dmMute";
import { isDmSoundEnabled, setDmSoundEnabled } from "../lib/dmSoundPref";
import { isPeerVerified } from "../lib/dmVerified";
import {
  VoiceMessageBubble,
  MediaMessageBubble,
  TextMessageBubble,
} from "./DmMessageBubbles";
import { NewChatDialog } from "./NewChatDialog";
import { SafetyNumberDialog } from "./SafetyNumberDialog";
import { loadIdentity } from "../lib/identity";
import { signedGet } from "../lib/signedRequest";
import { uploadAudioBlob, uploadDmAttachment } from "../lib/postsApi";

const API = process.env.REACT_APP_BACKEND_URL || "";

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  return sameDay
    ? d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
}

function fmtSecs(ms) {
  const s = Math.max(0, Math.floor((ms || 0) / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

// ---- Voice recorder (dedicated to DMs, minimal) -------------------------
// Independent from the topic feed recorder because DM voice payload has a
// hard size ceiling: after encryption + upload we still fit the plaintext
// reference (audioKey + iv + ms) into the 16 KB DM ciphertext limit.
// Exposes a live audio level history so the UI can draw a real waveform
// while recording — same UX as the ComposerBar in the main feed.
const LEVELS_MAX = 40;

function computeRms(sample) {
  let sum = 0;
  for (let i = 0; i < sample.length; i++) {
    const v = (sample[i] - 128) / 128;
    sum += v * v;
  }
  return Math.sqrt(sum / sample.length);
}

function useDmVoiceRecorder() {
  const [state, setState] = useState({ recording: false, blob: null, ms: 0 });
  const [levels, setLevels] = useState([]);
  const [seconds, setSeconds] = useState(0);
  const streamRef = useRef(null);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const startTsRef = useRef(0);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const tickRef = useRef(null);

  const teardownAudio = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (ctxRef.current) { ctxRef.current.close().catch((err) => devDebug("AudioContext close failed", err?.message)); ctxRef.current = null; }
    analyserRef.current = null;
  }, []);

  const stop = useCallback(() => {
    const mr = mrRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    teardownAudio();
  }, [teardownAudio]);

  const start = useCallback(async () => {
    setState({ recording: false, blob: null, ms: 0 });
    setLevels([]);
    setSeconds(0);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      throw new Error("Il browser non supporta la registrazione audio");
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (AC) {
      const ctx = new AC();
      ctxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.fftSize);
      const loop = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(buf);
        const rms = Math.min(1, computeRms(buf) * 4);
        setLevels((prev) => {
          const next = prev.length >= LEVELS_MAX ? prev.slice(1) : prev.slice();
          next.push(rms);
          return next;
        });
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    }
    const mr = new MediaRecorder(stream);
    mrRef.current = mr;
    chunksRef.current = [];
    mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
      const ms = Date.now() - startTsRef.current;
      setState({ recording: false, blob, ms });
    };
    startTsRef.current = Date.now();
    mr.start();
    setState({ recording: true, blob: null, ms: 0 });
    tickRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
  }, []);

  const reset = useCallback(() => {
    setState({ recording: false, blob: null, ms: 0 });
    setLevels([]);
    setSeconds(0);
    chunksRef.current = [];
  }, []);

  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    teardownAudio();
  }, [teardownAudio]);

  return { ...state, levels, seconds, start, stop, reset };
}

// ---- UI sub-components --------------------------------------------------

function ProfileHeader({ onBack }) {
  return (
    <div className="border-b border-edge bg-cream">
      <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center gap-3">
        <button
          onClick={onBack}
          className="w-9 h-9 rounded-full hover:bg-white flex items-center justify-center"
          data-testid="dm-back"
          aria-label="Torna al profilo"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="font-display font-semibold text-lg">Messaggi privati</div>
        <span className="ml-2 text-[11px] uppercase tracking-widest text-muted flex items-center gap-1">
          <Lock size={11} /> Cifrati end-to-end
        </span>
      </div>
    </div>
  );
}

function ConversationItem({ conv, active, onOpen, myIdentityId, muteTick, verifyTick }) {
  const initial = (conv.peer.displayName || "?").slice(0, 1).toUpperCase();
  const unread = conv.unreadCount || 0;
  // `muteTick` is bumped whenever any mute toggle happens elsewhere so
  // this component re-reads the localStorage-backed mute state without
  // needing a global store or a `storage` event listener.
  const muted = React.useMemo(() => isConversationMuted(conv.id), [conv.id, muteTick]);
  // Same pattern for the "verified in person" flag: bumped by
  // SafetyNumberDialog via ConversationPane so the sidebar reflects the
  // change immediately after the user marks/unmarks a peer.
  const verifiedInPerson = React.useMemo(
    () => isPeerVerified(conv.peer.identityId),
    [conv.peer.identityId, verifyTick],
  );
  const [preview, setPreview] = useState(null);

  // Decrypt the last message ciphertext client-side so we can show a
  // small preview under the peer name. Runs whenever the last message
  // changes (id + sentAt as the cheap tuple).
  useEffect(() => {
    let cancelled = false;
    const lm = conv.lastMessage;
    const peerPub = conv.peer.x25519PublicKeyHex;
    if (!lm || !peerPub) { setPreview(null); return; }
    (async () => {
      // SEC-001 enforcing: se il peer ha una firma DM invalida rifiuta
      // la decifra (potrebbe essere una chiave sostituita dal server).
      try { await requirePeerBinding(conv.peer); } catch {
        if (!cancelled) setPreview("⚠️ chiave non verificata");
        return;
      }
      const plain = await decryptFromPeer(peerPub, lm.ciphertextB64, lm.nonceB64);
      if (cancelled) return;
      if (!plain) { setPreview("🔒 impossibile decifrare"); return; }
      // Voice DM plaintext is a JSON reference — surface that as a label
      // rather than a raw JSON string.
      if (plain.startsWith("{\"type\":\"voice\"")) {
        setPreview("🎙 Messaggio vocale");
        return;
      }
      const oneLine = plain.replace(/\s+/g, " ").trim();
      setPreview(oneLine.length > 40 ? `${oneLine.slice(0, 40)}…` : oneLine);
    })();
    return () => { cancelled = true; };
  }, [conv.lastMessage, conv.peer.x25519PublicKeyHex]);

  const iSent = conv.lastMessage?.senderId === myIdentityId;

  return (
    <button
      onClick={() => onOpen(conv)}
      data-testid={`dm-conv-${conv.peer.identityId}`}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-left transition ${
        active ? "bg-white border border-edge shadow-sm" : "hover:bg-white/70"
      }`}
    >
      {conv.peer.avatar ? (
        <img
          src={conv.peer.avatar}
          alt=""
          className="w-10 h-10 rounded-full object-cover border border-edge shrink-0"
        />
      ) : (
        <div className="w-10 h-10 rounded-full bg-ink text-white flex items-center justify-center font-semibold text-sm shrink-0">
          {initial}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            className={`text-sm truncate ${unread > 0 ? "font-bold text-ink" : "font-semibold text-ink"}`}
          >
            {conv.peer.displayName || "Utente"}
          </span>
          {verifiedInPerson && (
            <ShieldCheck
              size={12}
              className="text-emerald-600 shrink-0"
              data-testid={`dm-conv-verified-${conv.peer.identityId}`}
              aria-label="Verificato di persona"
            />
          )}
          {muted && (
            <VolumeX
              size={12}
              className="text-muted shrink-0"
              data-testid={`dm-muted-icon-${conv.peer.identityId}`}
            />
          )}
        </div>
        {preview ? (
          <div
            className={`text-[11px] truncate ${unread > 0 ? "text-ink font-medium" : "text-muted"}`}
            data-testid={`dm-preview-${conv.peer.identityId}`}
          >
            {iSent && <span className="text-muted">Tu: </span>}
            {preview}
          </div>
        ) : (
          <div className="text-[11px] text-muted truncate font-mono">
            {conv.peer.identityId.slice(0, 12)}…
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <div className="text-[10px] text-muted tabular-nums">
          {formatTime(conv.lastMessageAt)}
        </div>
        {unread > 0 && (
          <span
            className="min-w-[18px] h-[18px] px-1 bg-primary text-white text-[10px] font-bold rounded-full flex items-center justify-center"
            data-testid={`dm-unread-${conv.peer.identityId}`}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </div>
    </button>
  );
}

// NewChatDialog is extracted to ./NewChatDialog.js (see imports at top).

// VoiceMessageBubble and TextMessageBubble are extracted into
// ./DmMessageBubbles.js (see imports at the top).

function ConversationPane({ conv, myIdentityId, onReadUpdate, onMuteChange, onVerifyChange }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [messageTtl, setMessageTtl] = useState(conv?.messageTtlSeconds ?? null);
  const [muted, setMuted] = useState(() => isConversationMuted(conv?.id));
  const scrollRef = useRef(null);
  const rec = useDmVoiceRecorder();
  const [uploadingVoice, setUploadingVoice] = useState(false);

  const [privateChatMediaEnabled, setPrivateChatMediaEnabled] =
    useState(false);
  const [uploadingPrivateMedia, setUploadingPrivateMedia] =
    useState(false);

  const dmImageInputRef = useRef(null);
  const dmVideoInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    let attempts = 0;

    const MAX_ATTEMPTS = 20;
    const RETRY_MS = 500;

    const loadPrivateMediaEntitlement = async () => {
      if (cancelled) return;

      attempts += 1;

      try {
        const identity = loadIdentity();
        const identityId = identity?.identityId;

        if (!identityId) {
          if (attempts < MAX_ATTEMPTS && !cancelled) {
            retryTimer = window.setTimeout(
              loadPrivateMediaEntitlement,
              RETRY_MS
            );
          }
          return;
        }

        const response = await signedGet(
          "/api/v1/payments/entitlement"
        );

        const payload = response?.data || {};

        const allowed =
          Boolean(payload?.active) &&
          (
            payload?.plan === "premium_plus" ||
            payload?.plan === "creator_pro"
          );

        if (!cancelled) {
          setPrivateChatMediaEnabled(allowed);
        }
      } catch (error) {
        if (attempts < MAX_ATTEMPTS && !cancelled) {
          retryTimer = window.setTimeout(
            loadPrivateMediaEntitlement,
            RETRY_MS
          );
        } else if (!cancelled) {
          console.error(
            "Entitlement Premium DM non disponibile:",
            error
          );
          setPrivateChatMediaEnabled(false);
        }
      }
    };

    const refreshPremiumEntitlement = () => {
      if (cancelled) return;

      attempts = 0;

      if (retryTimer) {
        window.clearTimeout(retryTimer);
        retryTimer = null;
      }

      void loadPrivateMediaEntitlement();
    };

    void loadPrivateMediaEntitlement();

    window.addEventListener(
      "focus",
      refreshPremiumEntitlement
    );

    return () => {
      cancelled = true;

      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }

      window.removeEventListener(
        "focus",
        refreshPremiumEntitlement
      );
    };
  }, [conv?.id]);

  useEffect(() => {
    setMuted(isConversationMuted(conv?.id));
    setMessageTtl(conv?.messageTtlSeconds ?? null);
  }, [conv?.id, conv?.messageTtlSeconds]);

  // SEC-001: verify the peer's DM key is signed by their Ed25519 identity
  // and compute a safety number for out-of-band verification.
  const [verifyState, setVerifyState] = useState({ verified: null, safety: "", myX25519: "" });
  const [showSafety, setShowSafety] = useState(false);
  const [manuallyVerified, setManuallyVerified] = useState(() => isPeerVerified(conv?.peer?.identityId));
  useEffect(() => {
    setManuallyVerified(isPeerVerified(conv?.peer?.identityId));
  }, [conv?.peer?.identityId]);
  useEffect(() => {
    if (!conv?.peer) { setVerifyState({ verified: null, safety: "", myX25519: "" }); return; }
    let cancelled = false;
    (async () => {
      const ok = await assertPeerBinding(conv.peer);
      if (cancelled) return;
      let safety = "";
      let myX25519 = "";
      if (ok) {
        try {
          const mine = await getMyDmKeys();
          myX25519 = mine.publicKeyHex;
          safety = computeSafetyNumber(mine.publicKeyHex, conv.peer.x25519PublicKeyHex);
        } catch (err) { devDebug("safety number failed", err?.message); }
      }
      setVerifyState({ verified: ok, safety, myX25519 });
    })();
    return () => { cancelled = true; };
  }, [conv?.peer]);

  const toggleMute = useCallback(() => {
    if (!conv) return;
    const next = toggleConversationMuted(conv.id);
    setMuted(next);
    onMuteChange?.();
  }, [conv, onMuteChange]);

  // Decrypt a stored DM message; parse voice payload if present.
  // SEC-001 enforcing: rifiuta la decifra se conv.peer.x25519SignatureHex
  // è presente ma non valido (chiave sostituita). Legacy peer senza firma
  // resta supportato (backward-compat migrazione).
  const decryptMessage = useCallback(async (raw, peerPub) => {
    if (!peerPub) return { ...raw, kind: "text", plaintext: "🔒 chiave del destinatario mancante" };
    try { await requirePeerBinding(conv.peer); } catch {
      return { ...raw, kind: "text", plaintext: "⚠️ Chiave DM del contatto non verificata (SEC-001) · messaggio ignorato" };
    }
    const plain = await decryptFromPeer(peerPub, raw.ciphertextB64, raw.nonceB64);
    if (plain == null) return { ...raw, kind: "text", plaintext: "🔒 impossibile decifrare" };
    if (plain.startsWith("{\"type\":\"voice\"")) {
      try {
        const p = JSON.parse(plain);
        if (p && p.type === "voice" && typeof p.audioKey === "string" && typeof p.audioIv === "string") {
          return { ...raw, kind: "voice", audioKey: p.audioKey, audioIv: p.audioIv, durationMs: p.ms || 0 };
        }
      } catch (err) {
        devDebug("DM voice payload parse failed, treating as text", err?.message);
      }
    }
    
      if (
        plain.startsWith("{\"type\":\"image\"") ||
        plain.startsWith("{\"type\":\"video\"")
      ) {
        try {
          const p = JSON.parse(plain);

          if (
            p &&
            (p.type === "image" || p.type === "video") &&
            typeof p.attachmentKey === "string" &&
            typeof p.attachmentIv === "string"
          ) {
            return {
              ...raw,
              kind: p.type,
              attachmentKey: p.attachmentKey,
              attachmentIv: p.attachmentIv,
              mimeType:
                typeof p.mimeType === "string"
                  ? p.mimeType
                  : "",
              fileName:
                typeof p.fileName === "string"
                  ? p.fileName
                  : "",
            };
          }
        } catch (err) {
          devDebug(
            "DM media payload parse failed",
            err?.message
          );
        }
      }

return { ...raw, kind: "text", plaintext: plain };
  }, [conv]);

  const load = useCallback(async () => {
    if (!conv) return;
    setLoading(true);
    try {
      const raw = await fetchMessages(conv.id);
      const peerPub = conv.peer.x25519PublicKeyHex || "";
      const decrypted = await Promise.all(raw.map((m) => decryptMessage(m, peerPub)));
      setMessages(decrypted);
      setError(null);
      // Fire-and-forget: mark this conversation as read now that we
      // finished loading. Bubble the fresh timestamp up so the sidebar can
      // decrement its badge without waiting for the next poll cycle.
      try {
        await markConversationRead(conv.id);
        onReadUpdate?.(conv.id);
      } catch (readErr) {
        devDebug("markConversationRead failed", readErr?.message);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, [conv, decryptMessage, onReadUpdate]);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendText = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || !conv || sending) return;
    if (!conv.peer.x25519PublicKeyHex) {
      setError("Impossibile cifrare: chiave del destinatario mancante.");
      return;
    }
    setSending(true);
    setError(null);
    try {
      // SEC-001 enforcing: rifiuta invio se la firma della chiave DM del
      // destinatario è presente ma non valida. Legacy peers (no signature
      // ancora) restano supportati durante la migrazione.
      await requirePeerBinding(conv.peer);
      const { ciphertextB64, nonceB64 } = await encryptForPeer(conv.peer.x25519PublicKeyHex, trimmed);
      const sent = await sendEncryptedMessage(conv.id, ciphertextB64, nonceB64);
      setMessages((prev) => [...prev, { ...sent, kind: "text", plaintext: trimmed }]);
      setText("");
    } catch (e) {
      if (e instanceof PeerBindingError) {
        setError(e.message);
      } else {
        setError(e?.response?.data?.message || e?.message || "Invio fallito");
      }
    } finally {
      setSending(false);
    }
  }, [text, conv, sending]);

  const sendVoice = useCallback(async () => {
    if (!rec.blob || !conv || uploadingVoice) return;
    if (!conv.peer.x25519PublicKeyHex) {
      setError("Impossibile cifrare: chiave del destinatario mancante.");
      return;
    }
    setUploadingVoice(true);
    setError(null);
    try {
      // SEC-001 enforcing (vedi sendText).
      await requirePeerBinding(conv.peer);
      // 1. Encrypt the raw audio bytes with the DM shared secret.
      const raw = new Uint8Array(await rec.blob.arrayBuffer());
      const { ciphertextBytes, nonceB64: audioIv } = await encryptBytesForPeer(
        conv.peer.x25519PublicKeyHex,
        raw,
      );
      // 2. Upload as opaque audio bytes — server sees ciphertext only.
      const encBlob = new Blob([ciphertextBytes], { type: "audio/webm" });
      const audioKey = await uploadAudioBlob(encBlob);
      if (!audioKey) throw new Error("Upload audio fallito");
      // 3. Send a normal DM message whose plaintext is the JSON reference.
      const payload = JSON.stringify({ type: "voice", audioKey, audioIv, ms: rec.ms });
      const { ciphertextB64, nonceB64 } = await encryptForPeer(conv.peer.x25519PublicKeyHex, payload);
      const sent = await sendEncryptedMessage(
        conv.id,
        ciphertextB64,
        nonceB64,
        audioKey,
      );
      setMessages((prev) => [
        ...prev,
        { ...sent, kind: "voice", audioKey, audioIv, durationMs: rec.ms },
      ]);
      rec.reset();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Invio vocale fallito");
    } finally {
      setUploadingVoice(false);
    }
  }, [rec, conv, uploadingVoice]);


  const sendPrivateMedia = useCallback(
    async (file) => {
      if (
        !file ||
        !conv ||
        !privateChatMediaEnabled ||
        uploadingPrivateMedia
      ) {
        return;
      }

      const kind = file.type?.startsWith("video/")
        ? "video"
        : file.type?.startsWith("image/")
        ? "image"
        : null;

      if (!kind) {
        setError("Sono consentite solo immagini e video.");
        return;
      }

      const MAX_PRIVATE_MEDIA_BYTES = 80 * 1024 * 1024;

      if (file.size > MAX_PRIVATE_MEDIA_BYTES) {
        setError("Allegato troppo grande. Limite massimo 80 MB.");
        return;
      }

      setUploadingPrivateMedia(true);
      setError("");

      try {
        await requirePeerBinding(conv.peer);

        const raw = new Uint8Array(
          await file.arrayBuffer()
        );

        const {
          ciphertextBytes,
          nonceB64: attachmentIv,
        } = await encryptBytesForPeer(
          conv.peer.x25519PublicKeyHex,
          raw
        );

        const encryptedBlob = new Blob(
          [ciphertextBytes],
          {
            type: "application/octet-stream",
          }
        );

        const attachmentKey =
          await uploadDmAttachment(
            encryptedBlob,
            `dm-${kind}-${Date.now()}.bin`
          );

        const safeMime =
          String(file.type || "").slice(0, 100);

        const safeName =
          String(file.name || "").slice(0, 180);

        const payload = JSON.stringify({
          type: kind,
          attachmentKey,
          attachmentIv,
          mimeType: safeMime,
          fileName: safeName,
        });

        const {
          ciphertextB64,
          nonceB64,
        } = await encryptForPeer(
          conv.peer.x25519PublicKeyHex,
          payload
        );

        const sent = await sendEncryptedMessage(
          conv.id,
          ciphertextB64,
          nonceB64,
          attachmentKey
        );

        setMessages((prev) => [
          ...prev,
          {
            ...sent,
            kind,
            attachmentKey,
            attachmentIv,
            mimeType: safeMime,
            fileName: safeName,
          },
        ]);
      } catch (e) {
        setError(
          e?.response?.data?.message ||
          e?.response?.data?.detail ||
          e?.message ||
          "Invio allegato fallito"
        );
      } finally {
        setUploadingPrivateMedia(false);
      }
    },
    [
      conv,
      privateChatMediaEnabled,
      uploadingPrivateMedia,
    ]
  );

  const changeTimer = useCallback(async (ttlSeconds) => {
    if (!conv) return;
    try {
      const saved = await setConversationTimer(conv.id, ttlSeconds);
      setMessageTtl(saved);
      setMenuOpen(false);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Impossibile aggiornare il timer");
    }
  }, [conv]);

  const deleteMessage = useCallback(async (message, scope) => {
    if (!conv) return;
    try {
      await deleteEncryptedMessage(conv.id, message.id, scope);
      setMessages((prev) => prev.filter((m) => m.id !== message.id));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Cancellazione fallita");
    }
  }, [conv]);

  const toggleMic = async () => {
    setError(null);
    if (rec.recording) {
      rec.stop();
      return;
    }
    if (rec.blob) {
      rec.reset();
      return;
    }
    try { await rec.start(); }
    catch (e) { setError(e?.message || "Impossibile accedere al microfono"); }
  };

  if (!conv) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-muted p-8 text-center">
        <MessageCircle size={40} className="mb-3 text-edge" />
        <div className="font-semibold text-ink mb-1">Nessuna conversazione aperta</div>
        <div className="text-xs max-w-xs">
          Seleziona una chat sulla sinistra oppure premi "Nuova" per iniziare.
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-w-0 min-h-0" data-testid="dm-conversation">
      <div className="border-b border-edge px-4 py-3 flex items-center gap-3 bg-white">
        {conv.peer.avatar ? (
          <img src={conv.peer.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-ink text-white flex items-center justify-center text-xs font-semibold">
            {(conv.peer.displayName || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <div className="text-sm font-semibold text-ink truncate">{conv.peer.displayName || "Utente"}</div>
            {muted && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted"
                data-testid="dm-conv-muted-flag"
              >
                <VolumeX size={11} /> Silenziata
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted flex items-center gap-1 flex-wrap">
            <Lock size={10} /> Cifrato E2E · <span className="font-mono">{conv.peer.identityId.slice(0, 12)}…</span>
            {verifyState.verified === true && manuallyVerified && (
              <button
                type="button"
                onClick={() => setShowSafety(true)}
                className="inline-flex items-center gap-1 ml-1 text-emerald-700 hover:underline"
                data-testid="dm-peer-verified-inperson"
                title={`Verificato di persona · Numero di sicurezza: ${verifyState.safety}`}
              >
                <ShieldCheck size={11} /> verificato di persona
              </button>
            )}
            {verifyState.verified === true && !manuallyVerified && (
              <button
                type="button"
                onClick={() => setShowSafety(true)}
                className="inline-flex items-center gap-1 ml-1 text-emerald-700 hover:underline"
                data-testid="dm-peer-verified"
                title={`Chiave DM firmata dall'identità. Confronta il numero di sicurezza per verificare di persona.`}
              >
                <ShieldCheck size={11} /> verificato · verifica di persona
              </button>
            )}
            {verifyState.verified === false && (
              <button
                type="button"
                onClick={() => setShowSafety(true)}
                className="inline-flex items-center gap-1 ml-1 text-amber-700 hover:underline"
                data-testid="dm-peer-unverified"
                title="La chiave DM del contatto non è ancora firmata dalla sua identità. La cifratura procede, ma un server compromesso potrebbe teoricamente sostituire la chiave."
              >
                <ShieldAlert size={11} /> non verificato
              </button>
            )}
          </div>
          {verifyState.verified === true && verifyState.safety && (
            <button
              type="button"
              onClick={() => setShowSafety(true)}
              className="mt-0.5 text-[10px] text-muted font-mono select-all hover:text-ink text-left"
              data-testid="dm-safety-number"
              title="Numero di sicurezza: confrontalo con il tuo contatto per essere sicuri che nessuno stia intercettando la conversazione. Tocca per aprire il QR."
            >
              🔐 {verifyState.safety}
            </button>
          )}
        </div>
        <div className="relative shrink-0">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="w-9 h-9 rounded-full hover:bg-cream flex items-center justify-center text-muted hover:text-ink"
            data-testid="dm-conv-menu"
            aria-label="Menu conversazione"
          >
            <MoreVertical size={16} />
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-11 z-30 w-56 rounded-2xl border border-edge bg-white shadow-lg p-1"
              onMouseLeave={() => setMenuOpen(false)}
              data-testid="dm-conv-menu-panel"
            >
              <button
                onClick={() => { toggleMute(); setMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-cream text-left text-sm text-ink"
                data-testid="dm-conv-toggle-mute"
              >
                {muted ? <Bell size={14} /> : <BellOff size={14} />}
                {muted ? "Riattiva notifiche" : "Silenzia notifiche"}
              </button>
              <div className="border-t border-edge my-1" />
              <div className="px-3 pt-1 pb-1 text-[10px] uppercase tracking-widest text-muted flex items-center gap-1">
                <Clock3 size={11} /> Messaggi a scomparsa
              </div>
              {[
                [null, "Disattivati"],
                [86400, "1 giorno"],
                [604800, "7 giorni"],
                [2592000, "30 giorni"],
                [7776000, "90 giorni"],
                [31536000, "1 anno"],
              ].map(([seconds, label]) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => void changeTimer(seconds)}
                  className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg hover:bg-cream text-left text-xs ${messageTtl === seconds ? "font-bold text-primary" : "text-ink"}`}
                  data-testid={`dm-timer-${seconds ?? "off"}`}
                >
                  <span>{label}</span>{messageTtl === seconds ? "✓" : ""}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2 bg-cream/40">
        {loading && messages.length === 0 && (
          <div className="text-xs text-muted flex items-center gap-2 justify-center py-6">
            <Loader2 size={13} className="animate-spin" /> Sto decifrando…
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-xs text-muted text-center py-6">
            Nessun messaggio ancora. Scrivi il primo — verrà cifrato prima di uscire dal tuo dispositivo.
          </div>
        )}
        {messages.map((m) => (
          m.kind === "voice"
            ? <VoiceMessageBubble
                key={m.id}
                msg={m}
                mine={m.senderId === myIdentityId}
                peerX25519={conv.peer.x25519PublicKeyHex}
                onDelete={deleteMessage}
              />
            : (m.kind === "image" || m.kind === "video")
            ? <MediaMessageBubble
                key={m.id}
                msg={m}
                mine={m.senderId === myIdentityId}
                peerX25519={conv.peer.x25519PublicKeyHex}
                onDelete={deleteMessage}
              />
            : <TextMessageBubble
                key={m.id}
                msg={m}
                mine={m.senderId === myIdentityId}
                onDelete={deleteMessage}
              />
        ))}
      </div>

      {error && <div className="text-xs text-primary px-4 py-1 bg-primary/5" data-testid="dm-error">{error}</div>}

      {rec.blob && !uploadingVoice && (
        <div
          className="border-t border-edge bg-cream/60 px-3 py-2 flex items-center gap-2"
          data-testid="dm-voice-preview"
        >
          <span className="text-[11px] uppercase tracking-widest text-primary font-semibold shrink-0">Voce pronta · {fmtSecs(rec.ms)}</span>
          <audio src={URL.createObjectURL(rec.blob)} controls className="flex-1 h-8 min-w-0" />
          <button
            onClick={rec.reset}
            className="w-8 h-8 rounded-full text-muted hover:text-primary flex items-center justify-center"
            aria-label="Scarta"
            data-testid="dm-voice-discard"
          >
            <Trash2 size={14} />
          </button>
          
          <button
            onClick={sendVoice}
            className="brutal-send"
            data-testid="dm-voice-send"
          >
            <Send size={14} /> Invia vocale
          </button>
        </div>
      )}

      <div className="border-t border-edge bg-white p-3 flex items-center gap-2 shrink-0" data-testid="dm-composer">
        <button
          onClick={toggleMic}
          className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
            rec.recording ? "bg-primary text-white animate-pulse" : "bg-ink text-white hover:bg-black"
          }`}
          title={rec.recording ? "Ferma registrazione" : "Registra un vocale cifrato"}
          data-testid="dm-mic"
        >
          {rec.recording ? <Square size={16} /> : <Mic size={16} />}
        </button>
{privateChatMediaEnabled && (
            <>
              <input
                ref={dmImageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="dm-image-input"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";

                  if (file) {
                    void sendPrivateMedia(file);
                  }
                }}
              />

              <input
                ref={dmVideoInputRef}
                type="file"
                accept="video/*"
                className="hidden"
                data-testid="dm-video-input"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";

                  if (file) {
                    void sendPrivateMedia(file);
                  }
                }}
              />

              <button
                type="button"
                onClick={() =>
                  dmImageInputRef.current?.click()
                }
                disabled={
                  uploadingPrivateMedia ||
                  rec.recording
                }
                className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-primary bg-primary px-3 text-xs font-semibold text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="dm-image-btn"
                title="Invia immagine cifrata"
              >
            <ImageIcon size={15} strokeWidth={2} aria-hidden="true" />
            <span>Immagine</span>
          </button>

              <button
                type="button"
                onClick={() =>
                  dmVideoInputRef.current?.click()
                }
                disabled={
                  uploadingPrivateMedia ||
                  rec.recording
                }
                className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-primary bg-primary px-3 text-xs font-semibold text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="dm-video-btn"
                title="Invia video cifrato"
              >
            <VideoIcon size={15} strokeWidth={2} aria-hidden="true" />
            <span>Video</span>
          </button>
            </>
          )}


        {rec.recording ? (
          <div
            className="flex-1 flex items-center gap-2 px-3 py-2 rounded-full bg-primary/5 border border-primary/20 min-w-0"
            data-testid="dm-live-waveform"
          >
            <div className="waveform flex-1" style={{ height: 22 }}>
              {rec.levels.map((l, i) => (
                <span
                  key={`dm-live-${i}`}
                  className="on"
                  style={{ height: `${Math.max(2, Math.round(l * 20))}px` }}
                />
              ))}
              {rec.levels.length === 0 && (
                <span className="text-[10px] text-primary/70 self-center">Sto ascoltando…</span>
              )}
            </div>
            <span className="text-[11px] tabular-nums text-primary font-semibold shrink-0">
              {fmtSecs(rec.seconds * 1000)}
            </span>
          </div>
        ) : (
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 1000))}
            onFocus={(e) => { const input = e.currentTarget; window.setTimeout(() => input.scrollIntoView({ block: "nearest" }), 120); }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void sendText();
              }
            }}
            placeholder="Scrivi un messaggio cifrato…"
            disabled={uploadingVoice}
            className="flex-1 px-4 py-2 rounded-full border border-edge bg-cream text-sm outline-none focus:border-ink focus:ring-2 focus:ring-ink/10 disabled:opacity-50"
            data-testid="dm-input"
          />
        )}
        <button
          onClick={sendText}
          disabled={sending || uploadingVoice || !text.trim() || rec.recording}
          className="brutal-send disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="dm-send"
        >
          <Send size={14} /> {sending ? "…" : "Invia"}
        </button>
      </div>
      {showSafety && (
        <SafetyNumberDialog
          peer={conv.peer}
          myX25519Hex={verifyState.myX25519}
          peerX25519Hex={conv.peer.x25519PublicKeyHex}
          safetyNumber={verifyState.safety}
          verified={verifyState.verified}
          onClose={() => setShowSafety(false)}
          onVerifiedChange={(next) => { setManuallyVerified(next); onVerifyChange?.(); }}
        />
      )}
    </div>
  );
}

export function DMView({ onBack, initialPeerId }) {
  const identity = useMemo(() => loadIdentity(), []);
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [muteTick, setMuteTick] = useState(0);
  const [verifyTick, setVerifyTick] = useState(0);
  const [searchQ, setSearchQ] = useState("");
  const [soundOn, setSoundOn] = useState(() => isDmSoundEnabled());
  const [visualHeight, setVisualHeight] = useState(null);
  const bumpMuteTick = useCallback(() => setMuteTick((v) => v + 1), []);
  const bumpVerifyTick = useCallback(() => setVerifyTick((v) => v + 1), []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const viewport = window.visualViewport;
    const syncHeight = () => {
      const next = Math.round(viewport?.height || window.innerHeight || 0);
      if (next > 0) setVisualHeight(next);
    };
    syncHeight();
    viewport?.addEventListener("resize", syncHeight);
    viewport?.addEventListener("scroll", syncHeight);
    window.addEventListener("orientationchange", syncHeight);
    return () => {
      viewport?.removeEventListener("resize", syncHeight);
      viewport?.removeEventListener("scroll", syncHeight);
      window.removeEventListener("orientationchange", syncHeight);
    };
  }, []);

  const toggleSound = useCallback(() => {
    setSoundOn((prev) => {
      const next = !prev;
      setDmSoundEnabled(next);
      return next;
    });
  }, []);

  // Client-side search across the loaded conversation list. We match on the
  // peer pseudonym and identityId; decrypted previews live inside each
  // ConversationItem so we do not surface them here (matching pseudonym is
  // the primary user need and keeps this filter O(n) without extra state).
  const filteredConversations = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => {
      const name = (c.peer?.displayName || "").toLowerCase();
      const id = (c.peer?.identityId || "").toLowerCase();
      return name.includes(q) || id.startsWith(q);
    });
  }, [conversations, searchQ]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      await getMyDmKeys().catch((err) => devDebug("getMyDmKeys reload failed", err?.message));
      const items = await listConversations();
      setConversations(items);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // If we arrive with `initialPeerId` (from "Manda un messaggio" in the
  // mini-profile), open the conversation as soon as we have our DM keys.
  useEffect(() => {
    if (!initialPeerId) return;
    let cancelled = false;
    (async () => {
      try {
        await getMyDmKeys().catch((err) => devDebug("getMyDmKeys reload failed", err?.message));
        const conv = await openConversation(initialPeerId);
        if (!cancelled) {
          setConversations((prev) => {
            const exists = prev.find((c) => c.id === conv.id);
            return exists ? prev.map((c) => (c.id === conv.id ? conv : c)) : [conv, ...prev];
          });
          setActive(conv);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || "Impossibile aprire la chat");
      }
    })();
    return () => { cancelled = true; };
  }, [initialPeerId]);

  const onConversationOpened = useCallback((conv) => {
    setConversations((prev) => {
      const exists = prev.find((c) => c.id === conv.id);
      return exists ? prev.map((c) => (c.id === conv.id ? conv : c)) : [conv, ...prev];
    });
    setActive(conv);
    setShowNew(false);
  }, []);

  const clearUnread = useCallback((conversationId) => {
    setConversations((prev) => prev.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0 } : c)));
  }, []);

  return (
    <div className="bg-cream flex flex-col overflow-hidden" data-testid="dm-view" style={{ "--dm-visual-height": visualHeight ? `${visualHeight}px` : "100dvh" }}>
      <ProfileHeader onBack={onBack} />
      <div className="flex-1 min-h-0 max-w-[1600px] w-full mx-auto px-2 sm:px-6 py-2 sm:py-4">
        <div
          className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-4 h-full min-h-0"
          data-mobile-view={active ? "conversation" : "list"}
        >
          <aside
            className={`bg-cream/70 rounded-3xl border border-edge p-3 flex flex-col overflow-hidden ${
              active ? "hidden md:flex" : "flex"
            }`}
            data-testid="dm-sidebar"
          >
            <button
              onClick={() => setShowNew(true)}
              className="brutal-btn primary !py-2 !text-sm mb-3 flex items-center justify-center gap-2"
              data-testid="dm-new-btn"
            >
              <Plus size={14} /> Nuova conversazione
            </button>
            <button
              onClick={toggleSound}
              className="w-full mb-3 flex items-center justify-center gap-2 py-1.5 rounded-full border border-edge bg-white text-[11px] text-muted hover:text-ink hover:border-ink transition"
              data-testid="dm-sound-toggle"
              aria-pressed={soundOn}
              aria-label={soundOn ? "Disattiva suono nuovi messaggi" : "Attiva suono nuovi messaggi"}
              title={soundOn ? "Suono attivo per nuovi messaggi in background" : "Suono disattivato"}
            >
              {soundOn ? <Volume2 size={12} /> : <VolumeX size={12} />}
              {soundOn ? "Suono nuovi DM attivo" : "Suono nuovi DM disattivato"}
            </button>
            <div className="relative mb-3">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                type="text"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Cerca nelle chat…"
                className="w-full pl-8 pr-8 py-1.5 rounded-full border border-edge bg-white text-xs outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                data-testid="dm-search-input"
                aria-label="Cerca nelle conversazioni"
              />
              {searchQ && (
                <button
                  onClick={() => setSearchQ("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-ink text-xs"
                  data-testid="dm-search-clear"
                  aria-label="Cancella ricerca"
                >
                  ✕
                </button>
              )}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted px-2 mb-1">Chat</div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {loading && (
                <div className="text-xs text-muted flex items-center gap-2 px-2 py-2">
                  <Loader2 size={12} className="animate-spin" /> Carico…
                </div>
              )}
              {!loading && conversations.length === 0 && (
                <div className="text-xs text-muted px-2 py-4">
                  Nessuna conversazione. Premi "Nuova" per iniziare.
                </div>
              )}
              {!loading && conversations.length > 0 && filteredConversations.length === 0 && (
                <div
                  className="text-xs text-muted px-2 py-4"
                  data-testid="dm-search-empty"
                >
                  Nessuna chat corrisponde a "{searchQ}".
                </div>
              )}
              {filteredConversations.map((c) => (
                <ConversationItem
                  key={c.id}
                  conv={c}
                  active={active?.id === c.id}
                  onOpen={setActive}
                  myIdentityId={identity?.identityId}
                  muteTick={muteTick}
                  verifyTick={verifyTick}
                />
              ))}
            </div>
            {error && <div className="text-[11px] text-primary px-2 py-2" data-testid="dm-list-error">{error}</div>}
          </aside>
          <section
            className={`bg-white rounded-3xl border border-edge flex-col overflow-hidden min-h-0 ${
              active ? "flex" : "hidden md:flex"
            }`}
          >
            {active && (
              <button
                onClick={() => setActive(null)}
                className="md:hidden flex items-center gap-2 text-xs text-muted hover:text-ink px-3 pt-2 shrink-0"
                data-testid="dm-back-to-list"
              >
                <ArrowLeft size={14} /> Torna all'elenco
              </button>
            )}
            <ConversationPane
              key={active?.id || "none"}
              conv={active}
              myIdentityId={identity?.identityId}
              onReadUpdate={clearUnread}
              onMuteChange={bumpMuteTick}
              onVerifyChange={bumpVerifyTick}
            />
          </section>
        </div>
      </div>
      {showNew && (
        <NewChatDialog
          onClose={() => setShowNew(false)}
          onOpened={onConversationOpened}
          myIdentityId={identity?.identityId}
        />
      )}
    </div>
  );
}
